/**
 * parentApi.ts  -  TanStack Query hooks for the parent portal.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChildSummary {
  id: string;
  displayName: string;
  username: string;
  avatarConfig: { colour: string; hat: string; accessories: string[] };
  ageGroup: string;
  classMembers: {
    class: {
      id: string;
      name: string;
      yearGroup: string;
      teacher: { firstName: string; lastName: string };
    };
    joinedAt: string;
  }[];
  streak: { currentStreak: number } | null;
}

export interface ChildBadge {
  slug: string;
  name: string;
  description: string;
  category: string;
  xpBonus: number;
  earnedAt: string;
}

export interface ChildProgress {
  childId: string;
  displayName: string;
  totalXp: number;
  streak: { current: number; longest: number };
  mastery: {
    grapheme: string;
    phoneme: string;
    phase: number;
    masteryLevel: number;
    attemptCount: number;
    lastPractisedAt: string;
  }[];
  recentWork: {
    id: string;
    lesson: { id: string; title: string; grapheme: string };
    stars: number;
    tags: string[];
    submittedAt: string;
    scoredAt: string;
  }[];
  badges: ChildBadge[];
}

export type ActivityItem =
  | {
      type: "xp_earned";
      amount: number;
      source: string;
      label: string;
      at: string;
    }
  | { type: "badge_earned"; badge: string; emoji: string; at: string }
  | { type: "lesson_done"; lesson: string; at: string }
  | { type: "streak"; days: number; at: string }
  | {
      type: "session_attended";
      className: string;
      teacherName: string;
      durationMins: number;
      at: string;
    };

export interface ParentThread {
  threadId: string;
  student: { id: string; displayName: string };
  teacherName: string;
  lastMessage: string;
  lastMessageAt: string;
  lastSender: "teacher" | "parent";
  unreadCount: number;
}

export interface ParentMessage {
  id: string;
  sender: "teacher" | "parent";
  body: string;
  sentAt: string;
  readAt: string | null;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const parentKeys = {
  children: () => ["parent", "children"] as const,
  progress: (id: string) => ["parent", "child", id, "progress"] as const,
  activity: (id: string) => ["child-activity", id] as const,
  threads: () => ["parent", "messages", "threads"] as const,
  thread: (id: string) => ["parent", "messages", "thread", id] as const,
  unread: () => ["parent", "messages", "unread"] as const,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useParentChildren() {
  return useQuery({
    queryKey: parentKeys.children(),
    queryFn: () => api.get<{ children: ChildSummary[] }>("/parent/children"),
    staleTime: 60_000,
  });
}

export function useChildProgress(childId: string) {
  return useQuery({
    queryKey: parentKeys.progress(childId),
    queryFn: () =>
      api.get<ChildProgress>(`/parent/children/${childId}/progress`),
    staleTime: 120_000,
    enabled: !!childId,
  });
}

export function useChildActivity(studentId: string) {
  return useQuery({
    queryKey: parentKeys.activity(studentId),
    queryFn: () =>
      api
        .get<{
          activity: ActivityItem[];
        }>(`/parent/children/${studentId}/activity`)
        .then((r) => r.activity ?? []),
    staleTime: 60_000,
    enabled: !!studentId,
  });
}

export function useParentThreads() {
  return useQuery({
    queryKey: parentKeys.threads(),
    queryFn: () => api.get<{ threads: ParentThread[] }>("/parent/messages"),
    staleTime: 30_000,
  });
}

export function useParentThread(threadId: string) {
  return useQuery({
    queryKey: parentKeys.thread(threadId),
    queryFn: () =>
      api.get<{ messages: ParentMessage[] }>(`/parent/messages/${threadId}`),
    staleTime: 10_000,
    enabled: !!threadId,
  });
}

export function useParentUnreadCount() {
  return useQuery({
    queryKey: parentKeys.unread(),
    queryFn: async () => {
      const data = await api.get<{ threads: ParentThread[] }>(
        "/parent/messages",
      );
      return data.threads.reduce((sum, t) => sum + t.unreadCount, 0);
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useParentReply(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post(`/parent/messages/${threadId}/reply`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: parentKeys.thread(threadId) });
      qc.invalidateQueries({ queryKey: parentKeys.threads() });
      qc.invalidateQueries({ queryKey: parentKeys.unread() });
    },
  });
}

// ─── Child management mutations ───────────────────────────────────────────────

export function useCreateChild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      displayName: string;
      dateOfBirth: string;
      locale?: string;
    }) => api.post<{ child: { id: string } }>("/parent/children", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: parentKeys.children() }),
  });
}

export function useSetChildPin() {
  return useMutation({
    mutationFn: ({ childId, pin }: { childId: string; pin: string }) =>
      api.patch(`/parent/children/${childId}/pin`, { pin }),
  });
}

export function useJoinClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      childId,
      joinCode,
    }: {
      childId: string;
      joinCode: string;
    }) =>
      api.post<{ message: string; className: string; teacherName: string }>(
        `/parent/children/${childId}/join-class`,
        { joinCode },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: parentKeys.children() }),
  });
}

// ─── Class feed (approved media submissions) ──────────────────────────────────

export interface FeedItem {
  id: string;
  mediaUrl: string | null;
  taskType: string;
  taskPrompt: string;
  lessonTitle: string;
  grapheme: string;
  className: string;
  stars: number | null;
  submittedAt: string;
  approvedAt: string | null;
}

export interface ChildFeedResponse {
  studentId: string;
  displayName: string;
  feed: FeedItem[];
}

export function useChildFeed(studentId: string) {
  return useQuery({
    queryKey: ["parent", "feed", studentId],
    queryFn: () =>
      api.get<ChildFeedResponse>(`/parent/children/${studentId}/feed`),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Upcoming assignments ─────────────────────────────────────────────────────

export interface UpcomingAssignment {
  id: string;
  lessonTitle: string;
  grapheme: string;
  phase: number;
  className: string;
  dueDate: string | null;
  assignedAt: string;
}

export interface ChildUpcomingResponse {
  studentId: string;
  displayName: string;
  upcoming: UpcomingAssignment[];
}

export function useChildUpcoming(studentId: string) {
  return useQuery({
    queryKey: ["parent", "upcoming", studentId],
    queryFn: () =>
      api.get<ChildUpcomingResponse>(`/parent/children/${studentId}/upcoming`),
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000,
  });
}
